import { registerNotificationDevicePane } from '#~/notifications/device-pane.js';
import { useAgents } from '#~/agent/hooks.js';
import { toDevice, toStatus } from '#~/device/utils.js';
import {
  orderSettingsTabs,
  useHasVisibleControls,
  useIsDeviceEmbedded,
  useSectionGroups
} from '#~/room/components/image/settings/hooks.js';
import { Icon } from '@studio/design-system/icon';
import type { HtmlAttributes } from '@studio/design-system/props';
import * as Tabs from '@studio/design-system/tabs';
import { useLatestFn } from '@studio/react-utils/hooks';
import { toEnum } from '@tryiris-ai/js-types';
import { useEffect, useMemo, type ReactElement } from 'react';
import { twMerge } from 'cn';
import { Footer } from './footer.jsx';
import { Header } from './header/header.jsx';
import { PREDEFINED_SECTIONS, SECTION_CONFIG } from './layout/main-section-config.js';
import { ScrollableMainTabs } from './layout/scrollable-main-tabs.js';
import { useProducer, useStore } from './store/hooks.js';
import { Panel } from './store/types.js';
import { initializeSectionStateForPanel } from './utils/section-state-utils.js';
import { getVendorContent } from '@studio/design-system/themes/vendor.js';
import { SUBCATEGORY_TRIGGER_CLASSNAME } from './types.js';
import { useVirtualPageview } from '#~/analytics/pageview/index.js';
import { SETTINGS_PANEL_MAPPINGS } from '#~/analytics/pageview/constants.js';
import type { SettingsPanelMappingKey } from '#~/analytics/pageview/types.js';

/** Video settings `<Scaffold>` component. */
export const Scaffold = ({ className, ...props }: HtmlAttributes): ReactElement => {
  const producer = useProducer();
  const panel = useStore((µ) => µ.panel);
  const agentId = useStore((µ) => µ.agentId);
  const agent = useAgents((a) => a.find((a) => a.id === agentId));
  const candidate = useStore((µ) => µ.candidate);
  // Mount/unmount follows the visible pane, so room selection never steals toast priority.
  useEffect(() => registerNotificationDevicePane(candidate.id), [candidate.id]);
  // Read the deep-link sub-tab once and pass it through `passthru` so
  // `<IrisConnectionSection>` can seed `activeTab` on mount. The store
  // value never changes after the initial render (sub-tab transitions
  // live in IrisConnectionSection's local `useState`), so this read is
  // effectively initialization-time.
  const defaultTab = useStore((µ) => µ.defaultTab);

  const overrides = useStore((µ) => µ.overrides);
  // TODO: ***fixme
  // @ts-ignore
  const deviceStatus = toStatus(toDevice(candidate));
  // TODO: end

  const dynamicSectionGroups = useSectionGroups(candidate.id);
  const isEmbedded = useIsDeviceEmbedded(candidate.id);
  // The Control tab's only content is the registry Controls group, so it is
  // withheld when that group has nothing visible — judged with the sheet's
  // unsaved edits so the strip agrees with the panel it would open.
  const hasVisibleControls = useHasVisibleControls(candidate.id, overrides);
  const predefinedSections = useMemo(() => PREDEFINED_SECTIONS, []);
  // One ordered union rather than [...predefined, ...dynamic]: a predefined
  // entry renders where CANONICAL_TAB_ORDER puts it, not where it was declared.
  // The embedded and empty-Controls rules are applied here, upstream of
  // activePanel below, so the device can never auto-correct onto a withheld tab.
  const finalSectionGroups = useMemo(
    () =>
      orderSettingsTabs(predefinedSections, dynamicSectionGroups, {
        hasVisibleControls,
        isEmbedded
      }),
    [dynamicSectionGroups, hasVisibleControls, isEmbedded, predefinedSections]
  );

  const handleChange = useLatestFn((nextValue: string) => {
    const nextPanel = toEnum(Panel, nextValue);

    if (nextPanel) {
      producer((draft) => {
        draft.panel = nextPanel;
        // Initialize section state for dynamic panels
        initializeSectionStateForPanel(draft, nextValue);
      });
    }
  });

  const activePanel = useMemo(() => {
    const isCurrentPanelValid = finalSectionGroups.some(
      (section) => section.panelEnumValue === panel
    );

    if (isCurrentPanelValid) {
      return panel;
    }

    return finalSectionGroups[0]?.panelEnumValue;
  }, [finalSectionGroups, panel]);

  // Auto-correct panel when current selection is invalid
  useEffect(() => {
    if (activePanel && activePanel !== panel) {
      handleChange(activePanel);
    }
  }, [activePanel, handleChange, panel]);

  // activePanel → canonical virtual screen. Two panels deliberately share one
  // canonical screen here (Video/VideoSettings, Controls/Control), and the old
  // panel-keyed effect emitted a second $pageview when the user moved between
  // them. useVirtualPageview keys the fire-once rule on the canonical path, so
  // those transitions now count as staying on the same screen.
  let mappingKey: SettingsPanelMappingKey | undefined;

  if (activePanel === Panel.Video || activePanel === Panel.VideoSettings) {
    mappingKey = 'advanced-settings-video';
  } else if (activePanel === Panel.Audio) {
    mappingKey = 'advanced-settings-audio';
  } else if (activePanel === Panel.Controls || activePanel === Panel.Control) {
    mappingKey = 'advanced-settings-control';
  } else if (activePanel === Panel.Network) {
    // Unreachable while no tab yields Panel.Network: the Network tab is retired
    // but the panel and its pageview mapping are kept, matching the hide-not-
    // remove approach taken on the registry side.
    mappingKey = 'advanced-settings-network';
  } else if (activePanel === Panel.Features) {
    mappingKey = 'advanced-settings-features';
  } else if (activePanel === Panel.System) {
    mappingKey = 'advanced-settings-system';
  } else if (activePanel === Panel.IrisConnection) {
    mappingKey = 'advanced-settings-iris-connection';
  }

  const pageviewMapping =
    mappingKey === undefined ? undefined : SETTINGS_PANEL_MAPPINGS[mappingKey];

  useVirtualPageview(pageviewMapping?.path ?? null, true, {
    ...(pageviewMapping === undefined ? {} : { category: pageviewMapping.category }),
    ...(pageviewMapping?.section === undefined ? {} : { section: pageviewMapping.section })
  });

  // Passthrough props for sub-components. `defaultTab` flows here so
  // the IrisConnection component (which lives behind a dynamic
  // `SECTION_CONFIG` lookup — not a direct render) can still receive
  // the deep-link target.
  //
  // Multi-Add Phase 2: `device` carries the candidate identity +
  // `agentStatus` derived from the agent record (the candidate itself
  // doesn't carry `agentStatus` — it's a frontend-extended Device
  // field, not part of the M_Device shape stored in the store). The
  // IrisConnectionSection's needs-attention hook reads this to derive
  // the per-sub-tab warning booleans. Older sections (Network /
  // Features / etc.) accept extra props transparently via `...props`.
  //
  // Wrapped in useMemo (adopted from extended-discovery) so `passthru`
  // keeps a STABLE reference — MainTabContent's useMemo lists it as a
  // dep, so a fresh object each render would defeat that memo
  // (referential-stability; cf. the §6 gating perf fix).
  const passthru = useMemo(
    () => ({
      // Deal with overflow clipping of focus rings
      // Padding (32px) + margin (8px) adds up to 40px
      className: 'm-8',
      agent,
      defaultTab,
      device: {
        id: candidate.id,
        modelId: candidate.modelId,
        agentStatus: agent?.status
      }
    }),
    [agent, defaultTab, candidate.id, candidate.modelId]
  );

  // Dynamically build main tab triggers
  const MainTabTriggers = useMemo(
    () => (
      <>
        {finalSectionGroups.map((sectionGroup) => {
          const config = SECTION_CONFIG[sectionGroup.sectionGroup] || {
            icon: 'camera',
            label: sectionGroup.sectionGroup,
            component: () => null
          };
          return (
            <Tabs.Trigger
              value={sectionGroup.panelEnumValue}
              key={sectionGroup.panelEnumValue}
              className={twMerge(SUBCATEGORY_TRIGGER_CLASSNAME, 'flex-shrink-0 px-0')}
            >
              {config.icon ? (
                <Icon icon={config.icon} className='!text-xl' />
              ) : (
                <img
                  alt='Vendor logo'
                  className='mr-[3px] h-[18px] w-[18px]'
                  src={getVendorContent({
                    iris: '/assets/icons/iris_badge_black_white.svg',
                    ptzoptics: '/assets/icons/hive_badge.png'
                  })}
                />
              )}
              <span
                className={`${activePanel === sectionGroup.panelEnumValue ? 'font-demibold' : 'font-medium'}`}
              >
                {config.label}
              </span>
            </Tabs.Trigger>
          );
        })}
      </>
    ),
    [activePanel, finalSectionGroups]
  );

  // Dynamically build main tab content
  const MainTabContent = useMemo(
    () => (
      <>
        {finalSectionGroups.map((sectionGroup) => {
          const config = SECTION_CONFIG[sectionGroup.sectionGroup] || { component: () => null };
          const Component = config.component;
          return (
            <Tabs.Content value={sectionGroup.panelEnumValue} key={sectionGroup.panelEnumValue}>
              {activePanel === sectionGroup.panelEnumValue && <Component {...passthru} />}
            </Tabs.Content>
          );
        })}
      </>
    ),
    [activePanel, finalSectionGroups, passthru]
  );

  return (
    <div className={twMerge('flex h-full w-sheet flex-col', className)} {...props}>
      <Header className='pb-16' />

      <Tabs.Root
        className='grow p-32 pt-16'
        onChange={handleChange}
        {...(activePanel ? { value: activePanel } : {})}
      >
        <ScrollableMainTabs>{MainTabTriggers}</ScrollableMainTabs>
        {MainTabContent}
      </Tabs.Root>

      <Footer />
    </div>
  );
};
